// File: src/components/general/Badge/Badge.tsx
// Purpose: General UI component or utility

import React from "react";
import styled from "styled-components";
import { Tooltip } from "../Tooltip";

interface BadgeProps {
  count: number;
  label?: string;
  color?: string;
}

const BadgeWrapper = styled.div<{ count: number }>`
  position: absolute;
  top: -8px;
  right: ${({ count }) => (count > 99 ? "-12px" : count > 10 ? "-10px" : "-4px")}; /* ✅ Wider for 99+ */
  transform: translate(50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledBadge = styled.span<{ count: number, color?:string }>`
  background: ${({ color, theme }) => color ? color : theme.colors.primary};
  color: ${({  theme }) => theme.colors.buttonText};
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  padding: 4px 6px;
  border-radius: ${({ count }) => (count > 99 ? "8px" : "50%")}; /* ✅ Less rounded for large numbers */
  min-width: ${({ count }) => (count > 99 ? "28px" : "18px")}; /* ✅ Wider for 99+ */
  min-height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.fonts.fontWeightBold};
  box-shadow: ${({ theme }) => theme.elevations.md};
  line-height: 1;
  white-space: nowrap; /* ✅ Prevents breaking */

  /* 📱 Mobile Optimizations */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: ${({ count }) => (count > 99 ? "24px" : "14px")};
    min-height: 14px;
    font-size: ${({ theme }) => theme.typography.overline.fontSize};
    padding: 3px 5px;
    border-radius: ${({ count }) => (count > 99 ? "6px" : "50%")}; /* ✅ Even less rounded on mobile */
  }
`;

export const Badge: React.FC<BadgeProps> = ({ count, label, color }) => {
  if (count <= 0) return null;
  if (label) {
    return (
        <Tooltip content={label || "New notifications"}>
          <BadgeWrapper count={count}>
            <StyledBadge count={count} color={color}>{count > 99 ? "99+" : count}</StyledBadge>
          </BadgeWrapper>
        </Tooltip>
      );
  }
  return (
      <BadgeWrapper count={count}>
        <StyledBadge count={count} color={color}>{count > 99 ? "99+" : count}</StyledBadge>
      </BadgeWrapper>
  );
};